import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteJobProduct } from '@/api/rest/resources/jobProducts';
import { JobProduct } from '@/api/rest/resources/types/jobProducts';
import { JOB_PRODUCTS_QUERY_KEY } from '@/hooks/useJobProducts';

type DeleteJobProductDialogProps = {
  jobProduct: JobProduct | null;
  onClose: () => void;
};

export const DeleteJobProductDialog = ({ jobProduct, onClose }: DeleteJobProductDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteJobProduct();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!jobProduct) return;

    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync({ pathVariables: { id: jobProduct.id } });

      queryClient.invalidateQueries({ queryKey: [JOB_PRODUCTS_QUERY_KEY] });
      toast({ type: 'success', title: t('shared.ndiProducts.toast.deleteSuccess') });
      onClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiProducts.toast.deleteError') });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={!!jobProduct} onOpenChange={onClose}>
      <DialogContent data-testid='delete-job-product-dialog'>
        <DialogHeader className='typography-h2 w-full text-error md:typography-h3 sm:text-center'>
          {t('shared.ndiProducts.deleteDialog.header')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          {t('shared.ndiProducts.deleteDialog.body', { name: jobProduct?.display_name })}
        </DialogBody>
        <DialogFooter className='justify-end'>
          <DialogClose asChild>
            <Button variant='text'>{t('global.ui.buttons.cancel')}</Button>
          </DialogClose>
          <Button onClick={handleDelete} loading={isDeleting} className='bg-error hover:bg-error-dark'>
            {t('global.ui.buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
